import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  Signal,
  ViewEncapsulation,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxButton } from '../button/button';
import { HxIconButton } from '../icon-button/icon-button';
import { HxIcon } from '../icon/icon';
import { HxProgressBar } from '../progress-bar/progress-bar';
import { HxTooltipDirective } from '../tooltip/tooltip';

/** Impersonation state interface - minimal shape needed for display */
export interface ImpersonationDisplayState {
  isActive: boolean;
  originalAdmin?: {
    userName: string;
    userId: string;
  };
  impersonatedUser?: {
    userName: string;
    userId: string;
  };
}

/** Offline status shape for optional offline banner display */
export interface OfflineStatusContext {
  isOnline: Signal<boolean> | boolean;
  showOnlineStatus?: Signal<boolean> | boolean;
}

/** Sync lifecycle state */
export type SyncBannerState = 'idle' | 'syncing' | 'success' | 'error';

/** Export readiness for failed sync assessments */
export interface SyncBannerExportStatus {
  canExport: boolean;
  reason?: string;
}

/** Sync status context for background sync replay progress and notifications */
export interface SyncStatusContext {
  state: Signal<SyncBannerState> | SyncBannerState;
  progress?: Signal<number | undefined> | number;
  totalItems?: Signal<number | undefined> | number;
  syncedItems?: Signal<number | undefined> | number;
  errorMessage?: Signal<string | undefined> | string;
  exportStatus?: Signal<SyncBannerExportStatus | undefined> | SyncBannerExportStatus;
}

/** Banner display mode identifier */
export type BannerDisplayMode = 'offline' | 'syncing' | 'sync-success' | 'sync-error' | 'impersonation' | 'none';

/** Visual icon indicator type */
export type BannerVisualType = 'pulse-rings' | 'pulse-dot' | 'icon';

/** Status badge on the right of the banner (e.g. Offline "Pending sync") */
export interface BannerStatusBadge {
  text: string;
  dotColor?: string;
}

/** Action configuration for primary banner button */
export interface BannerPrimaryButton {
  label: string;
  ariaLabel: string;
  iconName?: string;
  context: 'neutral' | 'brand' | 'positive' | 'negative' | 'caution' | 'info';
  action: 'endImpersonation' | 'resync';
}

/** Secondary banner action shown beside the primary button */
export interface BannerSecondaryButton {
  label: string;
  ariaLabel: string;
  iconName: string;
  tooltip: string;
  disabled: boolean;
  action: 'exportAssessments';
}

/** Unified banner view model for single-template rendering */
export interface UnifiedBannerConfig {
  mode: BannerDisplayMode;
  containerClass: string;
  visualType: BannerVisualType;
  iconName?: string;
  iconClass?: string;
  title: string;
  isTitleStrong: boolean;
  subtitle?: string;
  showProgress: boolean;
  progressValue: number;
  isIndeterminate: boolean;
  primaryButton?: BannerPrimaryButton;
  secondaryButton?: BannerSecondaryButton;
  statusBadge?: BannerStatusBadge;
  showNavigateToAppointments: boolean;
  ariaLabel: string;
}

/** Helper to resolve Signal<T> or primitive T */
function unwrapValue<T>(val: Signal<T> | T | undefined, fallback: T): T {
  if (val === undefined || val === null) {
    return fallback;
  }
  if (typeof val === 'function') {
    return (val as Signal<T>)();
  }
  return val;
}

/**
 * HxImpersonationIndicator Component
 *
 * Displays a prominent unified warning/status banner for:
 *
 * - Admin impersonation sessions (red)
 * - Offline mode warnings (amber)
 * - Background data synchronization with progress bar (blue)
 * - Sync success notifications with 3s auto-dismiss (green)
 * - Sync failure alerts with retry and appointments navigation (red/amber)
 *
 * Built as a pure presentational UI component using Angular signals and OnPush change detection.
 */
@Component({
  selector: 'hx-impersonation-indicator',
  standalone: true,
  imports: [HxIcon, HxButton, HxIconButton, HxProgressBar, HxTooltipDirective],
  templateUrl: './impersonation-indicator.html',
  styleUrl: './impersonation-indicator.css',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': '"hx-impersonation-indicator"',
    '[class.hx-impersonation-indicator--active]': 'bannerConfig().mode !== "none"',
    '[attr.hidden]': 'bannerConfig().mode === "none" ? true : null',
  },
})
export class HxImpersonationIndicator extends HxBaseControl {
  /** Impersonation state. When isActive is true, displays impersonation banner. */
  readonly impersonationState = input<ImpersonationDisplayState>({ isActive: false });

  /** Optional offline status context. When offline, takes highest priority. */
  readonly offlineStatus = input<OfflineStatusContext | undefined>(undefined);

  /** Optional sync status context for data replay progress, success, and error. */
  readonly syncStatus = input<SyncStatusContext | undefined>(undefined);

  /** Whether to show navigation arrow button to appointments page when sync fails. */
  readonly showNavigateToAppointments = input<boolean>(false);

  /** Width of the banner (CSS value: %, px, rem). */
  readonly width = input<string>('100%');

  /** Compact mode (single-line layout for constrained headers). */
  readonly compact = input<boolean>(false);

  /** Emitted when "End Impersonation" is clicked. */
  readonly endImpersonation = output<void>();

  /** Emitted when "Resync" is clicked on sync failure. */
  readonly resync = output<void>();

  /** Emitted when navigation icon button is clicked. */
  readonly navigateToAppointments = output<void>();

  /** Emitted when the export (download) button is clicked on sync failure. */
  readonly exportAssessments = output<void>();

  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private readonly router = inject(Router, { optional: true });

  /** Reactive signal tracking current URL */
  protected readonly currentUrl = toSignal(
    this.router?.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects || e.url)
    ) ?? of(''),
    {
      initialValue: this.router?.url || (typeof window !== 'undefined' ? window.location.pathname : ''),
    }
  );

  /** Check if current page is Dashboard or Appointments */
  protected readonly isDashboardOrAppointmentsPage = computed(() => {
    const url = (this.currentUrl() || '').toLowerCase();
    return url.includes('dashboard') || url.includes('appointment');
  });

  /**
   * Unified single source of truth computed configuration for the entire banner. Priority: Offline -> Sync
   * (syncing/success/error) -> Impersonation -> None.
   */
  readonly bannerConfig = computed<UnifiedBannerConfig>(() => {
    const offlineCtx = this.offlineStatus();
    const isOffline = offlineCtx ? !unwrapValue(offlineCtx.isOnline, true) : false;
    const showOnlineStatus = offlineCtx ? unwrapValue(offlineCtx.showOnlineStatus, true) : true;

    // 1. Offline Mode (Highest priority)
    if (isOffline && showOnlineStatus) {
      const subtitle = this.isDashboardOrAppointmentsPage()
        ? 'Only assessments that are available for offline can be accessed. Any changes you make will be synced automatically when your connection is restored.'
        : 'Any changes you make will be synced automatically when your connection is restored.';

      return {
        mode: 'offline',
        containerClass: 'hx-impersonation-indicator__banner--offline',
        visualType: 'icon',
        iconName: 'wifi-off',
        iconClass: 'hx-impersonation-indicator__icon--offline',
        title: 'Offline assessment in progress',
        isTitleStrong: true,
        subtitle,
        showProgress: false,
        progressValue: 0,
        isIndeterminate: false,
        statusBadge: {
          text: 'Pending sync',
          dotColor: 'var(--warning-warning-300)',
        },
        showNavigateToAppointments: false,
        ariaLabel: `Offline assessment in progress: ${subtitle}`,
      };
    }

    // 2. Sync Lifecycle Modes
    const syncCtx = this.syncStatus();
    const syncState = syncCtx ? unwrapValue(syncCtx.state, 'idle') : 'idle';

    if (syncState === 'syncing') {
      const progress = syncCtx?.progress !== undefined ? unwrapValue(syncCtx.progress, undefined) : undefined;
      const total = syncCtx?.totalItems !== undefined ? unwrapValue(syncCtx.totalItems, undefined) : undefined;
      const synced = syncCtx?.syncedItems !== undefined ? unwrapValue(syncCtx.syncedItems, undefined) : undefined;

      const subtitle =
        synced !== undefined && total !== undefined
          ? `Syncing record ${synced} of ${total}...`
          : 'Uploading offline changes to the server...';

      return {
        mode: 'syncing',
        containerClass: 'hx-impersonation-indicator__banner--syncing',
        visualType: 'icon',
        iconName: 'arrow-repeat',
        iconClass: 'hx-impersonation-indicator__icon--syncing animate-spin',
        title: 'Data sync in progress...',
        isTitleStrong: true,
        subtitle,
        showProgress: true,
        progressValue: progress ?? 0,
        isIndeterminate: progress === undefined,
        showNavigateToAppointments: false,
        ariaLabel: 'Data synchronization in progress',
      };
    }

    if (syncState === 'success') {
      return {
        mode: 'sync-success',
        containerClass: 'hx-impersonation-indicator__banner--sync-success',
        visualType: 'icon',
        iconName: 'check-circle',
        iconClass: 'hx-impersonation-indicator__icon--sync-success',
        title: 'Sync Success',
        isTitleStrong: true,
        subtitle: 'All pending changes have been synchronized.',
        showProgress: false,
        progressValue: 100,
        isIndeterminate: false,
        showNavigateToAppointments: false,
        ariaLabel: 'Data synchronization completed successfully',
      };
    }

    if (syncState === 'error') {
      const errorMsg = syncCtx?.errorMessage !== undefined ? unwrapValue(syncCtx.errorMessage, undefined) : undefined;
      const subtitle = errorMsg || 'Unable to sync offline changes. The API server may be unavailable.';

      return {
        mode: 'sync-error',
        containerClass: 'hx-impersonation-indicator__banner--sync-error',
        visualType: 'icon',
        iconName: 'cloud-slash',
        iconClass: 'hx-impersonation-indicator__icon--sync-error',
        title: 'Sync Failure',
        isTitleStrong: true,
        subtitle,
        showProgress: false,
        progressValue: 0,
        isIndeterminate: false,
        primaryButton: {
          label: 'Re sync',
          ariaLabel: 'Retry synchronizing pending changes',
          iconName: 'cloud-upload',
          context: 'neutral',
          action: 'resync',
        },
        secondaryButton: this.buildExportButton(syncCtx),
        showNavigateToAppointments: this.showNavigateToAppointments(),
        ariaLabel: `Sync failed: ${subtitle}`,
      };
    }

    // 3. Impersonation Mode
    const impState = this.impersonationState();
    if (impState && impState.isActive) {
      const userName = impState.impersonatedUser?.userName || 'Unknown User';
      const isCompact = this.compact();

      return {
        mode: 'impersonation',
        containerClass: 'hx-impersonation-indicator__banner--impersonation',
        visualType: 'pulse-rings',
        iconName: 'eye',
        title: `Viewing as: ${userName}`,
        isTitleStrong: false,
        subtitle: isCompact ? undefined : 'Simulation mode – changes will not be saved.',
        showProgress: false,
        progressValue: 0,
        isIndeterminate: false,
        primaryButton: {
          label: 'Stop Impersonation',
          ariaLabel: 'Stop impersonation and restore admin privileges',
          context: 'neutral',
          action: 'endImpersonation',
        },
        showNavigateToAppointments: false,
        ariaLabel: `Viewing as ${userName}. Simulation mode – changes will not be saved.`,
      };
    }

    // 4. Inactive
    return {
      mode: 'none',
      containerClass: '',
      visualType: 'icon',
      title: '',
      isTitleStrong: false,
      showProgress: false,
      progressValue: 0,
      isIndeterminate: false,
      showNavigateToAppointments: false,
      ariaLabel: '',
    };
  });

  /** Handle primary button click */
  protected handlePrimaryAction(action: 'endImpersonation' | 'resync'): void {
    if (action === 'endImpersonation') {
      void this.liveAnnouncer.announce('Ending impersonation session. Restoring admin privileges.', 'polite');
      this.endImpersonation.emit();
    } else if (action === 'resync') {
      void this.liveAnnouncer.announce('Retrying data synchronization.', 'polite');
      this.resync.emit();
    }
  }

  /** Handle secondary button click */
  protected handleSecondaryAction(button: BannerSecondaryButton): void {
    if (button.disabled) return;
    void this.liveAnnouncer.announce('Preparing assessment export.', 'polite');
    this.exportAssessments.emit();
  }

  private buildExportButton(syncCtx: SyncStatusContext | undefined): BannerSecondaryButton | undefined {
    if (syncCtx?.exportStatus === undefined) return undefined;
    const status = typeof syncCtx.exportStatus === 'function' ? syncCtx.exportStatus() : syncCtx.exportStatus;
    const canExport = !!status?.canExport;
    return {
      label: 'Download',
      ariaLabel: 'Download failed assessments as ZIP',
      iconName: 'download',
      tooltip: canExport ? 'Export as ZIP' : (status?.reason ?? 'Export unavailable'),
      disabled: !canExport,
      action: 'exportAssessments',
    };
  }
}
